import { z } from 'zod'

import { DashboardWidgetType } from '../enums'

import { subscriptionStatusSchema, subscriptionUsageSchema } from './subscription'

/**
 * Normalized DashboardWidget entity returned by the backend (matches DTODashboardWidgetResponse).
 * Visibility: INHERIT widgets are visible to everyone who can view the dashboard; RESTRICTED
 * widgets only to the listed roles/users (dashboard_widget:view grants).
 */
export const dashboardWidgetSchema = z.object({
  id: z.string().uuid(),
  dashboardId: z.string().uuid().nullable(),
  type: z.nativeEnum(DashboardWidgetType),
  title: z.string(),
  x: z.number().int(),
  y: z.number().int(),
  w: z.number().int(),
  h: z.number().int(),
  sortOrder: z.number().int().optional().default(0),
  dataSource: z.string(),
  dataSourceConfig: z.record(z.unknown()).optional().default({}),
  chartConfig: z.record(z.unknown()).optional().default({}),
  config: z.record(z.unknown()).optional().default({}),
  visibilityMode: z.enum(['INHERIT', 'RESTRICTED']).optional(),
  visibilityRoleIds: z.array(z.string().uuid()).optional(),
  visibilityUserIds: z.array(z.string().uuid()).optional(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
})

/**
 * Request to create a new widget. When visibilityRoleIds/userIds are omitted,
 * the server grants DASHBOARD_WIDGET_VIEW to all workspace roles (default-visible).
 */
export const dashboardWidgetCreateRequestSchema = z.object({
  type: z.nativeEnum(DashboardWidgetType),
  title: z.string(),
  x: z.number().int(),
  y: z.number().int(),
  w: z.number().int(),
  h: z.number().int(),
  sortOrder: z.number().int().optional(),
  dataSource: z.string(),
  dataSourceConfig: z.record(z.unknown()).optional(),
  chartConfig: z.record(z.unknown()).optional(),
  config: z.record(z.unknown()).optional(),
  visibilityRoleIds: z.array(z.string().uuid()).optional(),
  visibilityUserIds: z.array(z.string().uuid()).optional(),
})

/**
 * Partial update request for an existing widget. Null/omitted fields are not updated.
 */
export const dashboardWidgetUpdateRequestSchema = z.object({
  title: z.string().optional(),
  x: z.number().int().optional(),
  y: z.number().int().optional(),
  w: z.number().int().optional(),
  h: z.number().int().optional(),
  sortOrder: z.number().int().optional(),
  dataSource: z.string().optional(),
  dataSourceConfig: z.record(z.unknown()).optional(),
  chartConfig: z.record(z.unknown()).optional(),
  config: z.record(z.unknown()).optional(),
})

/**
 * Bulk-set widget visibility: the exact set of roles and/or users that should see the widget.
 * Matches the backend's DTOWidgetVisibilityRequest.
 */
export const widgetVisibilityRequestSchema = z.object({
  roleIds: z.array(z.string().uuid()),
  userIds: z.array(z.string().uuid()),
})

export const widgetReorderRequestSchema = z.object({
  widgetIds: z.array(z.string().uuid()),
})

/**
 * Normalized Dashboard entity returned by the backend (matches DTODashboardResponse).
 * No scopeType, scopeId, visibility fields — dashboards are visible to everyone
 * in the workspace by default. Widgets manage their own visibility via permission grants.
 */
export const dashboardSchema = z.object({
  id: z.string().uuid().nullable(),
  workspaceId: z.string().uuid(),
  creatorUserId: z.string().uuid().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  widgets: z.array(dashboardWidgetSchema).default([]),
  isDefault: z.boolean(),
  /** Set for a record group's own dashboard; its widgets only query that group. */
  recordGroupId: z.string().uuid().nullable().optional(),
  /** Group dashboards: also include the sub-groups the viewer may see. */
  includeSubgroups: z.boolean().optional().default(false),
  isEmpty: z.boolean().optional().default(false),
  isCreator: z.boolean().optional().default(false),
  /** Permission keys the current user holds on this dashboard. */
  allowedActions: z.array(z.string()).optional().default([]),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
})

export const dashboardCreateRequestSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional().nullable(),
  isDefault: z.boolean().optional(),
})

export const dashboardUpdateRequestSchema = dashboardCreateRequestSchema.partial().extend({
  includeSubgroups: z.boolean().optional(),
})

export const dashboardAnalyticsQueryRequestSchema = z.object({
  dataSource: z.string().min(1),
  config: z.record(z.unknown()).optional().default({}),
  /** The dashboard the widget belongs to; the server enforces a group dashboard's scope. */
  dashboardId: z.string().uuid().optional(),
})

export const metricItemSchema = z.object({
  label: z.string(),
  value: z.union([z.number(), z.string(), z.boolean()]),
})

export const seriesItemSchema = z.object({
  label: z.string(),
  value: z.union([z.number(), z.string()]),
})

export const activityFeedItemSchema = z.object({
  id: z.string(),
  recordId: z.string().uuid().optional(),
  actorName: z.string(),
  actionType: z.string(),
  note: z.string().optional().nullable(),
  fieldLabel: z.string().optional().nullable(),
  fieldType: z.string().optional().nullable(),
  oldValue: z.string().optional().nullable(),
  newValue: z.string().optional().nullable(),
  // A change to a field the viewer may not see: no field name or values
  redacted: z.boolean().optional(),
  createdAt: z.string(),
})

export const dashboardTableRowSchema = z.record(z.unknown())

export const dashboardAnalyticsResponseSchema = z.object({
  metrics: z.array(metricItemSchema).optional(),
  series: z.array(seriesItemSchema).optional(),
  items: z.array(z.union([activityFeedItemSchema, dashboardTableRowSchema])).optional(),
  status: subscriptionStatusSchema.optional(),
  usage: subscriptionUsageSchema.optional(),
  message: z.string().optional(),
  dataSource: z.string().optional(),
  hasMore: z.boolean().optional(),
  // The widget reads a field the viewer may not see
  restricted: z.boolean().optional(),
  lastCreatedAt: z.string().optional(),
  lastId: z.string().optional(),
}).passthrough()

export type DashboardWidget = z.infer<typeof dashboardWidgetSchema>
export type DashboardWidgetCreateRequest = z.infer<typeof dashboardWidgetCreateRequestSchema>
export type DashboardWidgetUpdateRequest = z.infer<typeof dashboardWidgetUpdateRequestSchema>
export type WidgetVisibilityRequest = z.infer<typeof widgetVisibilityRequestSchema>
export type WidgetReorderRequest = z.infer<typeof widgetReorderRequestSchema>
export type Dashboard = z.infer<typeof dashboardSchema>
export type DashboardCreateRequest = z.infer<typeof dashboardCreateRequestSchema>
export type DashboardUpdateRequest = z.infer<typeof dashboardUpdateRequestSchema>
export type DashboardAnalyticsQueryRequest = z.infer<typeof dashboardAnalyticsQueryRequestSchema>
export type DashboardAnalyticsResponse = z.infer<typeof dashboardAnalyticsResponseSchema>
export type MetricItem = z.infer<typeof metricItemSchema>
export type SeriesItem = z.infer<typeof seriesItemSchema>
export type ActivityFeedItem = z.infer<typeof activityFeedItemSchema>
